/**
 * 将 scj 真实接口数据同步到 platform store（API 优先，失败回退本地缓存）
 */

import { syncMaterialLibrary } from '@/services/materialStore';
import { syncTemplateLibrary } from '@/services/templateStore';
import {
  syncQueryMaterialLibrary,
  syncQueryTemplateLibrary,
} from '@/services/queryLibraryStore';
import { syncPaletteLibrary } from '@/services/paletteStore';
import { syncSystemConfigLibrary } from '@/services/configStore';
import {
  syncDesignCenterLibrary,
  DESIGN_CENTER_LIST_OP_TYPE,
} from '@/services/designCenterStore';
import { reactive } from 'vue';

export const USE_ART_API = import.meta.env.VITE_USE_ART_API !== 'false';
export const ART_API_CACHE_TTL_MS = 30_000;

export const VIEW_MODULE_MAP = {
  templates: 'queryTemplates',
  templates1: 'queryTemplates',
  materials: 'queryMaterials',
  materials1: 'queryMaterials',
  colorPalette: 'palettes',
  settings: 'config',
  studio: 'drafts',
  fonts: 'fonts',
};

export function getViewModuleKey(view) {
  return VIEW_MODULE_MAP[view] || null;
}

const MODULE_LABELS = {
  templates: '模板库',
  queryTemplates: '模板库',
  materials: '素材库',
  queryMaterials: '素材库',
  palettes: '色板',
  config: '系统配置',
  drafts: '设计中心',
};

function contextKey(moduleName, context = {}) {
  if (moduleName === 'config') return 'global';
  const { storeId, user, departmentId } = context;
  const userKey = user?.userId || user?.id || user?.username || user?.name || '';
  return `${storeId || 'all'}:${departmentId ?? 'none'}:${userKey}`;
}

export function createArtApiSync(
  platform,
  draftsRef,
  getContext = () => ({}),
  editorDraftCacheRef = null,
  { cacheTtlMs = ART_API_CACHE_TTL_MS, now = () => Date.now() } = {},
) {
  const apiStatus = reactive({
    templates: false,
    queryTemplates: false,
    materials: false,
    queryMaterials: false,
    palettes: false,
    config: false,
    drafts: false,
  });
  const moduleLoading = reactive({
    templates: false,
    queryTemplates: false,
    materials: false,
    queryMaterials: false,
    palettes: false,
    config: false,
    drafts: false,
    fonts: false,
  });
  const inFlight = new Map();
  const attemptedAt = new Map();
  const loadingCounts = new Map();

  function updateLoading(moduleName, delta) {
    const next = Math.max(0, (loadingCounts.get(moduleName) || 0) + delta);
    loadingCounts.set(moduleName, next);
    moduleLoading[moduleName] = next > 0;
  }

  function isFresh(key) {
    const timestamp = attemptedAt.get(key);
    return timestamp != null && now() - timestamp < cacheTtlMs;
  }

  function runModuleLoad(moduleName, context, loader, { force = false, cacheKeySuffix = '' } = {}) {
    if (!USE_ART_API) return Promise.resolve();
    const key = `${moduleName}:${contextKey(moduleName, context)}:${cacheKeySuffix}`;
    const pending = inFlight.get(key);
    if (pending) return pending;
    if (!force && isFresh(key)) return Promise.resolve({ cached: true });

    updateLoading(moduleName, 1);
    const task = Promise.resolve()
      .then(loader)
      .then((result) => {
        apiStatus[moduleName] = true;
        attemptedAt.set(key, now());
        return result;
      })
      .catch((error) => {
        apiStatus[moduleName] = false;
        attemptedAt.set(key, now());
        if (import.meta.env.DEV) {
          console.warn(`[art-api] ${MODULE_LABELS[moduleName]}远程加载失败`, error);
        }
        return undefined;
      })
      .finally(() => {
        if (inFlight.get(key) === task) inFlight.delete(key);
        updateLoading(moduleName, -1);
      });
    inFlight.set(key, task);
    return task;
  }

  function loadTemplates(options = {}) {
    const context = getContext() || {};
    return runModuleLoad('templates', context, async () => {
      const { storeId, user } = context;
      await syncTemplateLibrary(platform, storeId, user, context);
    }, options);
  }

  function loadMaterials(options = {}) {
    const context = getContext() || {};
    return runModuleLoad('materials', context, async () => {
      const { storeId, user } = context;
      await syncMaterialLibrary(platform, storeId, user, context);
    }, options);
  }

  function loadQueryTemplates(options = {}) {
    const context = getContext() || {};
    return runModuleLoad('queryTemplates', context, async () => {
      const { storeId, user } = context;
      await syncQueryTemplateLibrary(platform, storeId, user, context);
    }, options);
  }

  function loadQueryMaterials(options = {}) {
    const context = getContext() || {};
    return runModuleLoad('queryMaterials', context, async () => {
      const { storeId, user } = context;
      await syncQueryMaterialLibrary(platform, storeId, user, context);
    }, options);
  }

  function loadPalettes(options = {}) {
    const context = getContext() || {};
    return runModuleLoad('palettes', context, async () => {
      const { storeId, user } = context;
      await syncPaletteLibrary(platform, storeId, user, context);
    }, options);
  }

  function loadConfig(options = {}) {
    const context = getContext() || {};
    return runModuleLoad('config', context, async () => {
      await syncSystemConfigLibrary(platform);
    }, options);
  }

  function loadDrafts(options = {}) {
    const context = getContext() || {};
    return runModuleLoad('drafts', context, async () => {
      const { storeId, user } = context;
      const localSavedDesigns = (platform.designs || []).filter(d =>
        !d.parentDesignId && (d.status === 'done' || d.remoteSaved),
      ).map(d => ({
        ...d,
        templateType: 1,
        status: 'done',
      }));
      const localDraftDesigns = (editorDraftCacheRef?.value || []).filter(d =>
        d?.id && d.status !== 'done' && d.templateType !== 1,
      ).map(d => ({
        ...d,
        templateType: 2,
        status: 'draft',
      }));
      await syncDesignCenterLibrary(draftsRef, storeId, user, {
        localSavedDesigns: [...localSavedDesigns, ...localDraftDesigns],
        opType: options.opType,
        pageNum: options.pageNum,
        pageSize: options.pageSize,
        searchDesignName: options.searchDesignName,
        searchTemplateName: options.searchTemplateName,
        createUserId: options.createUserId,
        searchTotalsRef: platform.designCenterSearchTotals,
        searchTotalKey: options.searchTotalKey,
        countsRef: platform.designCenterCounts,
        ...context,
      });
    }, {
      ...options,
      cacheKeySuffix: [
        options.opType ? `opType=${options.opType}` : 'all',
        options.searchDesignName ? `q=${options.searchDesignName}` : '',
        options.pageNum ? `p=${options.pageNum}` : '',
        options.pageSize ? `ps=${options.pageSize}` : '',
      ].filter(Boolean).join(':') || 'all',
    });
  }

  async function loadTemplateFolders(options = {}) {
    await loadTemplates(options);
  }

  async function loadMaterialFolders(options = {}) {
    await loadMaterials(options);
  }

  /** 进入左侧导航模块时，按需拉取对应接口 */
  async function loadForView(view, options = {}) {
    if (!USE_ART_API || !view) return { ...apiStatus };
    switch (view) {
      case 'templates':
      case 'templates1':
        await loadQueryTemplates(options);
        break;
      case 'materials':
      case 'materials1':
        await loadQueryMaterials(options);
        break;
      case 'fonts':
        break;
      case 'colorPalette':
        await loadPalettes(options);
        break;
      case 'settings':
        await loadConfig(options);
        break;
      case 'studio':
        await loadDrafts(options);
        break;
      default:
        break;
    }
    return { ...apiStatus };
  }

  async function syncAll(options = {}) {
    await Promise.allSettled([
      loadTemplates(options),
      loadMaterials(options),
      loadPalettes(options),
      loadConfig(options),
      loadDrafts(options),
    ]);
    return { ...apiStatus };
  }

  function invalidate(view) {
    const moduleName = VIEW_MODULE_MAP[view] || view;
    if (!moduleName) return;
    [...attemptedAt.keys()]
      .filter(key => key.startsWith(`${moduleName}:`))
      .forEach(key => attemptedAt.delete(key));
  }

  function withModuleLoading(moduleName, task) {
    if (!moduleName) return Promise.resolve().then(task);
    updateLoading(moduleName, 1);
    return Promise.resolve()
      .then(task)
      .finally(() => updateLoading(moduleName, -1));
  }

  return {
    apiStatus,
    moduleLoading,
    withModuleLoading,
    syncAll,
    loadForView,
    loadTemplates,
    loadQueryTemplates,
    loadMaterials,
    loadQueryMaterials,
    loadPalettes,
    loadConfig,
    loadDrafts,
    invalidate,
  };
}
